import { z } from 'zod';
import { ZONE_KIND_COLOR_KEYS } from '../constants/zone-kind-colors';
import { defineListQuery, listFilter } from './list-query.schemas';
import type { LngLat } from './organization.schemas';

/**
 * Pricing reference data: zones and named points rate lines key on
 * (gitaumoses4/chukua#21). Never free-text addresses — those won't match.
 */

/**
 * What a zone/named point can be — a deployment's own vocabulary (`zone_kinds`
 * table), not a hardcoded enum: this platform serves more than one tenant, and a
 * fixed list would have baked Index Cargo's corridor into every deployment.
 * `code` is what `ZoneInput.kind` matches against.
 */
export const zoneKindInputSchema = z.object({
  code: z.string().trim().min(1).max(32),
  name: z.string().trim().min(1).max(120),
  /** How the kind's zones are drawn on the Zones map — a key of ZONE_KIND_COLORS, never a hex. */
  color: z.enum(ZONE_KIND_COLOR_KEYS),
});
export type ZoneKindInput = z.infer<typeof zoneKindInputSchema>;

const geoJsonPositionSchema = z.tuple([z.number().min(-180).max(180), z.number().min(-90).max(90)]);

export const zoneGeoJsonPointSchema = z.object({
  type: z.literal('Point'),
  coordinates: geoJsonPositionSchema,
});
export type ZoneGeoJsonPoint = z.infer<typeof zoneGeoJsonPointSchema>;

/** RFC 7946: a polygon's linear rings must be closed — first and last position equal. A hand-built form can violate this. */
function ringIsClosed(ring: [number, number][]): boolean {
  const [first] = ring;
  const last = ring[ring.length - 1];
  return ring.length >= 4 && first[0] === last[0] && first[1] === last[1];
}

export const zoneGeoJsonPolygonSchema = z.object({
  type: z.literal('Polygon'),
  coordinates: z
    .array(z.array(geoJsonPositionSchema).min(4))
    .min(1)
    .refine((rings) => rings.every(ringIsClosed), { message: 'Each polygon ring must be closed (first and last position equal)' }),
});
export type ZoneGeoJsonPolygon = z.infer<typeof zoneGeoJsonPolygonSchema>;

/** A point zone's reach: from a weighbridge gate to a whole town. */
export const ZONE_RADIUS_METERS = { min: 10, max: 50_000, default: 500 } as const;

/**
 * A zone's area is a polygon, or a centroid and a radius for a place with no drawn boundary (a town, a
 * border). With a polygon, `centroid` is its centre and there is no radius. Its country is never sent:
 * the API works it out from the location (ZonesService, via the geocoding seam).
 */
export const zoneInputSchema = z
  .object({
    code: z.string().trim().min(1).max(32),
    name: z.string().trim().min(1).max(160),
    /** Matched against the zone-kind catalogue's `code` at write time — see ZonesService.assertValidKind. */
    kind: z.string().trim().min(1).max(32),
    polygon: zoneGeoJsonPolygonSchema.nullable().optional(),
    centroid: zoneGeoJsonPointSchema.nullable().optional(),
    radiusMeters: z.number().int().min(ZONE_RADIUS_METERS.min).max(ZONE_RADIUS_METERS.max).nullable().optional(),
  })
  .superRefine((zone, ctx) => {
    const isPoint = !zone.polygon && Boolean(zone.centroid);
    const hasRadius = zone.radiusMeters !== null && zone.radiusMeters !== undefined;
    if (isPoint && !hasRadius) ctx.addIssue({ code: 'custom', path: ['radiusMeters'], message: 'A point zone needs a radius' });
    if (!isPoint && hasRadius) ctx.addIssue({ code: 'custom', path: ['radiusMeters'], message: 'Only a point zone has a radius' });
  });
export type ZoneInput = z.infer<typeof zoneInputSchema>;

export const ZONE_STATUSES = ['active', 'inactive'] as const;
export type ZoneStatus = (typeof ZONE_STATUSES)[number];

/** How a zone is located: a traced polygon, a single named point, or neither yet. */
export const ZONE_SHAPES = ['polygon', 'point', 'none'] as const;
export type ZoneShape = (typeof ZONE_SHAPES)[number];

/** `GET /zones` — the Zones section's table. `kind` sorts by the kind's name, and filters by its code; `country` sorts by country code. */
export const zoneListQuerySchema = defineListQuery({
  sortFields: ['name', 'code', 'kind', 'country'],
  defaultSort: { sort: 'name', order: 'asc' },
  filters: {
    status: listFilter(z.enum(ZONE_STATUSES)),
    kind: listFilter(z.string().trim().min(1).max(32)),
  },
});
export type ZoneListQuery = z.infer<typeof zoneListQuerySchema>;

/** `GET /zones/map` — the same search and filters as the table, unpaged, so the map shows every zone they match rather than one page. */
export const zoneMapQuerySchema = zoneListQuerySchema.pick({ q: true, status: true, kind: true });
export type ZoneMapQuery = z.infer<typeof zoneMapQuerySchema>;

/** `GET /zones/place-search` — a place's name as typed when starting a zone, biased towards the map's centre. */
export const placeSearchQuerySchema = z.object({
  q: z.string().trim().min(2).max(100),
  lng: z.coerce.number().min(-180).max(180).optional(),
  lat: z.coerce.number().min(-90).max(90).optional(),
});
export type PlaceSearchQuery = z.infer<typeof placeSearchQuerySchema>;

/** What a place search result is: a business or site, a town or area, or a street address. */
export const PLACE_KINDS = ['business', 'area', 'address'] as const;
export type PlaceKind = (typeof PLACE_KINDS)[number];

/** A named place from the geocoder, to start a zone from. It has a centre and, for an area, an extent — never an outline. */
export interface PlaceMatch {
  /** The geocoder's own id, stable enough to key a list on. */
  id: string;
  name: string;
  /** Where it is, to tell same-named places apart: "General Kago Road, Thika, Kenya". */
  context: string;
  kind: PlaceKind;
  center: [number, number];
  /** `[west, south, east, north]`; null, or a single point, for a business or an address. */
  bbox: [number, number, number, number] | null;
  countryCode: string | null;
}

/** `GET /zones/map-config`: what a zones map needs before it draws. */
export interface ZonesMapConfig {
  /** Real MapTiler tiles, or null when this deployment hasn't set MAPTILER_API_KEY. */
  tileKey: string | null;
  /** The company this deployment serves, marked on the map (with its logo) and where it opens; null until its location is set. */
  company: { name: string; location: LngLat; logoUrl: string | null; logoDarkUrl: string | null } | null;
}
